// @ts-nocheck — third-party pixel init code; type safety not applicable here
/**
 * Universal tracking module.
 *
 * Usage:
 *   import { track } from '../lib/tracking';
 *   track('purchase', { value: 99.00, currency: 'USD', order_id: 'ORD-xxx' });
 *
 * The track() function automatically:
 *   - Fires browser-side pixels (Meta Pixel, GA4, GTM, TikTok, Snapchat, LinkedIn, Pinterest, X)
 *   - Notifies the backend to dispatch server-side events (Meta CAPI, GA4 MP, TikTok Events API)
 *   - Injects custom header/footer scripts on first load
 *
 * Domain guard: scripts are only injected when running on the configured
 * VITE_STOREFRONT_URL domain. This prevents accidental injection into the
 * admin panel or any other origin.
 */

// ---------------------------------------------------------------------------
// Domain guard — tracking only runs on the storefront domain
// ---------------------------------------------------------------------------

function resolveStorefrontHostname(): string | null {
  const raw = import.meta.env.VITE_STOREFRONT_URL as string | undefined;
  if (!raw) return null;
  try {
    return new URL(raw).hostname;
  } catch {
    return null;
  }
}

const _storefrontHostname = resolveStorefrontHostname();

/** Returns true when tracking is allowed on the current page. */
function isStorefrontDomain(): boolean {
  // If no domain is configured (local dev), allow all origins
  if (!_storefrontHostname) return true;
  const host = window.location.hostname;
  // Always allow localhost/127.x for development
  if (host === 'localhost' || host === '127.0.0.1') return true;
  return host === _storefrontHostname;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TrackingPayload = Record<string, unknown>;

interface ProviderConfig {
  enabled: boolean;
  pixel_id?: string;
  measurement_id?: string;
  container_id?: string;
  conversion_id?: string;
  conversion_label?: string;
  partner_id?: string;
  tag_id?: string;
  test_event_code?: string;
  script?: string;
}

export interface TrackingConfig {
  meta_pixel?: ProviderConfig;
  meta_capi?: ProviderConfig;
  ga4?: ProviderConfig;
  gtm?: ProviderConfig;
  google_ads?: ProviderConfig;
  tiktok?: ProviderConfig;
  snapchat?: ProviderConfig;
  linkedin?: ProviderConfig;
  pinterest?: ProviderConfig;
  twitter?: ProviderConfig;
  custom_header?: ProviderConfig;
  custom_footer?: ProviderConfig;
}

// ---------------------------------------------------------------------------
// Global declarations
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    ttq?: {
      load: (id: string) => void;
      page: () => void;
      track: (event: string, data?: Record<string, unknown>) => void;
    };
    snaptr?: (...args: unknown[]) => void;
    lintrk?: (...args: unknown[]) => void;
    _linkedin_partner_id?: string;
    pintrk?: (...args: unknown[]) => void;
    twq?: (...args: unknown[]) => void;
  }
}

// ---------------------------------------------------------------------------
// Internal store
// ---------------------------------------------------------------------------

let _config: TrackingConfig = {};
let _initialized = false;
const _pendingEvents: Array<[string, TrackingPayload]> = [];

// ---------------------------------------------------------------------------
// Event name maps
// ---------------------------------------------------------------------------

const META_EVENT_MAP: Record<string, string> = {
  page_view:        'PageView',
  product_view:     'ViewContent',
  category_view:    'ViewContent',
  search:           'Search',
  add_to_wishlist:  'AddToWishlist',
  add_to_cart:      'AddToCart',
  remove_from_cart: 'RemoveFromCart',
  begin_checkout:   'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase:         'Purchase',
  register:         'CompleteRegistration',
  contact:          'Contact',
  lead:             'Lead',
  subscribe:        'Subscribe',
};

const GA4_EVENT_MAP: Record<string, string> = {
  page_view:         'page_view',
  product_view:      'view_item',
  category_view:     'view_item_list',
  search:            'search',
  add_to_wishlist:   'add_to_wishlist',
  add_to_cart:       'add_to_cart',
  remove_from_cart:  'remove_from_cart',
  begin_checkout:    'begin_checkout',
  add_shipping_info: 'add_shipping_info',
  add_payment_info:  'add_payment_info',
  purchase:          'purchase',
  refund:            'refund',
  register:          'sign_up',
  login:             'login',
  lead:              'generate_lead',
};

const TIKTOK_EVENT_MAP: Record<string, string> = {
  page_view:        'ViewContent',
  product_view:     'ViewContent',
  category_view:    'Browse',
  search:           'Search',
  add_to_wishlist:  'AddToWishlist',
  add_to_cart:      'AddToCart',
  begin_checkout:   'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase:         'CompletePayment',
  register:         'CompleteRegistration',
  contact:          'Contact',
  lead:             'SubmitForm',
  subscribe:        'Subscribe',
};

const SNAPCHAT_EVENT_MAP: Record<string, string> = {
  page_view:     'PAGE_VIEW',
  product_view:  'VIEW_CONTENT',
  add_to_cart:   'ADD_CART',
  purchase:      'PURCHASE',
  register:      'SIGN_UP',
  search:        'SEARCH',
};

const PINTEREST_EVENT_MAP: Record<string, string> = {
  page_view:    'pagevisit',
  category_view:'viewcategory',
  search:       'search',
  add_to_cart:  'addtocart',
  purchase:     'checkout',
};

// ---------------------------------------------------------------------------
// Script injection helpers
// ---------------------------------------------------------------------------

function injectScript(src: string, id: string, onLoad?: () => void): void {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  if (onLoad) script.onload = onLoad;
  document.head.appendChild(script);
}

function injectInlineScript(code: string, id: string, position: 'head' | 'body' = 'head'): void {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.textContent = code;
  if (position === 'head') {
    document.head.appendChild(script);
  } else {
    document.body.appendChild(script);
  }
}

function injectHtmlString(html: string, id: string, position: 'head' | 'body' = 'head'): void {
  if (document.getElementById(id)) return;

  // Wrapper element so we can guard against double-injection
  const wrapper = document.createElement('div');
  wrapper.id = id;
  const target = position === 'head' ? document.head : document.body;
  target.appendChild(wrapper);

  // innerHTML does NOT execute <script> tags; we must re-create them.
  const temp = document.createElement('div');
  temp.innerHTML = html;
  Array.from(temp.childNodes).forEach((node) => {
    if (node instanceof HTMLScriptElement) {
      const script = document.createElement('script');
      Array.from(node.attributes).forEach((attr) =>
        script.setAttribute(attr.name, attr.value)
      );
      script.textContent = node.textContent;
      wrapper.appendChild(script);
    } else {
      wrapper.appendChild(node.cloneNode(true));
    }
  });
}

// ---------------------------------------------------------------------------
// Provider initializers
// ---------------------------------------------------------------------------

function initMetaPixel(cfg: ProviderConfig): void {
  if (!cfg.pixel_id) return;
  if (window.fbq) return;

  // Standard Meta Pixel base code
  const n = function (...args: unknown[]) {
    // @ts-expect-error Meta Pixel pattern
    n.callMethod ? n.callMethod(...args) : n.queue.push(args);
  };
  // @ts-expect-error Meta Pixel pattern
  n.push = n;
  // @ts-expect-error Meta Pixel pattern
  n.loaded = true;
  // @ts-expect-error Meta Pixel pattern
  n.version = '2.0';
  // @ts-expect-error Meta Pixel pattern
  n.queue = [];
  window.fbq = n as typeof window.fbq;
  if (!window._fbq) window._fbq = n;

  injectScript('https://connect.facebook.net/en_US/fbevents.js', 'meta-pixel-sdk');
  window.fbq!('init', cfg.pixel_id);
  if (cfg.test_event_code) {
    window.fbq!('set', 'testEventCode', cfg.test_event_code);
  }
}

function initGA4(cfg: ProviderConfig): void {
  if (!cfg.measurement_id) return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function () {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', cfg.measurement_id);

  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${cfg.measurement_id}`,
    'ga4-sdk'
  );
}

function initGTM(cfg: ProviderConfig): void {
  if (!cfg.container_id) return;
  if (document.getElementById('gtm-sdk')) return;

  // Head snippet
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  injectScript(
    `https://www.googletagmanager.com/gtm.js?id=${cfg.container_id}`,
    'gtm-sdk'
  );

  // Body noscript (GTM requires this inside <body>)
  const noscript = document.createElement('noscript');
  noscript.id = 'gtm-noscript';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${cfg.container_id}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function initGoogleAds(cfg: ProviderConfig): void {
  if (!cfg.conversion_id) return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = window.gtag ?? function () {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', cfg.conversion_id);

  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${cfg.conversion_id}`,
    'google-ads-sdk'
  );
}

function initTikTok(cfg: ProviderConfig): void {
  if (!cfg.pixel_id) return;
  if (window.ttq) return;

  // TikTok Pixel base code
  const e: typeof window.ttq = {
    load(id) { this._i = id; },
    page() { this._p = true; },
    track(event, data) { this._q = (this._q ?? []).concat([[event, data]]); },
  } as unknown as typeof window.ttq;
  window.ttq = e;
  window.ttq.load(cfg.pixel_id);
  window.ttq.page();

  injectScript('https://analytics.tiktok.com/i18n/pixel/events.js', 'tiktok-sdk');
}

function initSnapchat(cfg: ProviderConfig): void {
  if (!cfg.pixel_id) return;
  if (window.snaptr) return;

  const s = function (...args: unknown[]) {
    // @ts-expect-error Snapchat pattern
    s.handleRequest ? s.handleRequest(...args) : s.queue.push(args);
  };
  // @ts-expect-error Snapchat pattern
  s.queue = [];
  window.snaptr = s as typeof window.snaptr;
  window.snaptr!('init', cfg.pixel_id);

  injectScript('https://sc-static.net/scevent.min.js', 'snapchat-sdk');
}

function initLinkedIn(cfg: ProviderConfig): void {
  if (!cfg.partner_id) return;
  if (window._linkedin_partner_id) return;

  window._linkedin_partner_id = cfg.partner_id;
  window.lintrk = window.lintrk ?? function (...args: unknown[]) {
    // @ts-expect-error LinkedIn pattern
    window.lintrk.q = (window.lintrk.q ?? []).concat([args]);
  };

  injectScript('https://snap.licdn.com/li.lms-analytics/insight.min.js', 'linkedin-sdk');
}

function initPinterest(cfg: ProviderConfig): void {
  if (!cfg.tag_id) return;
  if (window.pintrk) return;

  const t = function (...args: unknown[]) {
    // @ts-expect-error Pinterest pattern
    t.queue.push(args);
  };
  // @ts-expect-error Pinterest pattern
  t.version = '3.0';
  // @ts-expect-error Pinterest pattern
  t.queue = [];
  window.pintrk = t as typeof window.pintrk;
  window.pintrk!('load', cfg.tag_id);
  window.pintrk!('page');

  injectScript('https://s.pinimg.com/ct/core.js', 'pinterest-sdk');
}

function initTwitter(cfg: ProviderConfig): void {
  if (!cfg.pixel_id) return;
  if (window.twq) return;

  const t = function (...args: unknown[]) {
    // @ts-expect-error Twitter pattern
    t.exe ? t.exe(...args) : t.queue.push(args);
  };
  // @ts-expect-error Twitter pattern
  t.version = '1.1';
  // @ts-expect-error Twitter pattern
  t.queue = [];
  window.twq = t as typeof window.twq;
  window.twq!('config', cfg.pixel_id);

  injectScript('https://static.ads-twitter.com/uwt.js', 'twitter-sdk');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize tracking from the config returned by the backend.
 * Called once by useTrackingInit hook.
 */
export function initTracking(config: TrackingConfig): void {
  // Never inject tracking scripts outside the storefront domain
  if (!isStorefrontDomain()) return;

  _config = config;

  if (config.meta_pixel?.enabled) initMetaPixel(config.meta_pixel);
  if (config.ga4?.enabled) initGA4(config.ga4);
  if (config.gtm?.enabled) initGTM(config.gtm);
  if (config.google_ads?.enabled) initGoogleAds(config.google_ads);
  if (config.tiktok?.enabled) initTikTok(config.tiktok);
  if (config.snapchat?.enabled) initSnapchat(config.snapchat);
  if (config.linkedin?.enabled) initLinkedIn(config.linkedin);
  if (config.pinterest?.enabled) initPinterest(config.pinterest);
  if (config.twitter?.enabled) initTwitter(config.twitter);

  if (config.custom_header?.enabled && config.custom_header.script) {
    injectHtmlString(config.custom_header.script, 'custom-header-scripts', 'head');
  }
  if (config.custom_footer?.enabled && config.custom_footer.script) {
    injectHtmlString(config.custom_footer.script, 'custom-footer-scripts', 'body');
  }

  _initialized = true;

  // Fire any events that were queued before init
  for (const [event, payload] of _pendingEvents) {
    track(event, payload);
  }
  _pendingEvents.length = 0;
}

/**
 * Fire a tracking event to all enabled providers.
 *
 * @param eventName  Standard event name (e.g. 'purchase', 'add_to_cart')
 * @param payload    Event-specific data (value, currency, order_id, etc.)
 */
export function track(eventName: string, payload: TrackingPayload = {}): void {
  if (!_initialized) {
    _pendingEvents.push([eventName, payload]);
    return;
  }

  const firedBrowserProviders: string[] = [];

  // Meta Pixel
  if (_config.meta_pixel?.enabled && window.fbq) {
    const mapped = META_EVENT_MAP[eventName] ?? eventName;
    window.fbq('track', mapped, {
      value: payload.value,
      currency: payload.currency ?? 'USD',
      content_ids: payload.content_ids,
      content_type: payload.content_type,
      num_items: payload.num_items,
      search_string: payload.search_string,
      order_id: payload.order_id,
    });
    firedBrowserProviders.push('meta_pixel');
  }

  // GA4
  if (_config.ga4?.enabled && window.gtag) {
    const mapped = GA4_EVENT_MAP[eventName] ?? eventName;
    window.gtag('event', mapped, {
      currency: payload.currency ?? 'USD',
      value: payload.value,
      transaction_id: payload.order_id,
      search_term: payload.search_string,
      items: payload.items,
    });
    firedBrowserProviders.push('ga4');
  }

  // GTM — push to dataLayer so GTM triggers can fire
  if (_config.gtm?.enabled && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        currency: payload.currency ?? 'USD',
        value: payload.value,
        transaction_id: payload.order_id,
        items: payload.items,
      },
      ...payload,
    });
    firedBrowserProviders.push('gtm');
  }

  // Google Ads — fire purchase conversion
  if (_config.google_ads?.enabled && window.gtag && eventName === 'purchase') {
    const cfg = _config.google_ads;
    window.gtag('event', 'conversion', {
      send_to: `${cfg.conversion_id}/${cfg.conversion_label}`,
      value: payload.value,
      currency: payload.currency ?? 'USD',
      transaction_id: payload.order_id,
    });
    firedBrowserProviders.push('google_ads');
  }

  // TikTok
  if (_config.tiktok?.enabled && window.ttq) {
    const mapped = TIKTOK_EVENT_MAP[eventName] ?? eventName;
    window.ttq.track(mapped, {
      content_id: (payload.content_ids as string[])?.[0],
      content_type: (payload.content_type as string) ?? 'product',
      currency: (payload.currency as string) ?? 'USD',
      value: payload.value as number,
      query: payload.search_string as string,
      order_id: payload.order_id as string,
    });
    firedBrowserProviders.push('tiktok');
  }

  // Snapchat
  if (_config.snapchat?.enabled && window.snaptr) {
    const mapped = SNAPCHAT_EVENT_MAP[eventName];
    if (mapped) {
      window.snaptr('track', mapped, {
        price: payload.value,
        currency: payload.currency ?? 'USD',
        transaction_id: payload.order_id,
      });
      firedBrowserProviders.push('snapchat');
    }
  }

  // Pinterest
  if (_config.pinterest?.enabled && window.pintrk) {
    const mapped = PINTEREST_EVENT_MAP[eventName];
    if (mapped) {
      window.pintrk('track', mapped, {
        value: payload.value,
        currency: payload.currency ?? 'USD',
        order_id: payload.order_id,
      });
      firedBrowserProviders.push('pinterest');
    }
  }

  // X (Twitter)
  if (_config.twitter?.enabled && window.twq) {
    const twMap: Record<string, string> = { purchase: 'Purchase', register: 'SignUp', page_view: 'PageView' };
    const mapped = twMap[eventName];
    if (mapped) {
      window.twq('event', mapped, {
        value: String(payload.value ?? ''),
        currency: (payload.currency as string) ?? 'USD',
      });
      firedBrowserProviders.push('twitter');
    }
  }

  // Notify backend — logs browser events + triggers server-side dispatch
  reportToBackend(eventName, payload, firedBrowserProviders);
}

/**
 * Convenience wrappers for the most common e-commerce events.
 */
export const events = {
  pageView: (url?: string) =>
    track('page_view', { url: url ?? window.location.href }),

  productView: (product: { id: string | number; name: string; price: number; currency?: string }) =>
    track('product_view', {
      content_ids: [String(product.id)],
      content_type: 'product',
      value: product.price,
      currency: product.currency ?? 'USD',
    }),

  addToCart: (item: { id: string | number; name: string; price: number; quantity?: number; currency?: string }) =>
    track('add_to_cart', {
      content_ids: [String(item.id)],
      content_type: 'product',
      value: item.price * (item.quantity ?? 1),
      currency: item.currency ?? 'USD',
      num_items: item.quantity ?? 1,
    }),

  beginCheckout: (value: number, currency = 'USD') =>
    track('begin_checkout', { value, currency }),

  purchase: (order: {
    order_id: string;
    value: number;
    currency?: string;
    items?: unknown[];
    email?: string;
    phone?: string;
    fbc?: string;
    fbp?: string;
  }) =>
    track('purchase', {
      order_id: order.order_id,
      value: order.value,
      currency: order.currency ?? 'USD',
      items: order.items,
      email: order.email,
      phone: order.phone,
      fbc: order.fbc,
      fbp: order.fbp,
    }),

  search: (query: string) =>
    track('search', { search_string: query }),

  register: () => track('register'),
  login: () => track('login'),
  contact: () => track('contact'),
  lead: () => track('lead'),
};

// ---------------------------------------------------------------------------
// Backend reporting
// ---------------------------------------------------------------------------

function reportToBackend(
  eventName: string,
  payload: TrackingPayload,
  browserProviders: string[]
): void {
  const apiBase = import.meta.env.VITE_API_URL ?? '';
  fetch(`${apiBase}/api/storefront/tracking-events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    keepalive: true, // survives page navigations
    body: JSON.stringify({
      event: eventName,
      payload,
      browser_providers: browserProviders,
    }),
  }).catch(() => {
    // Never let tracking errors surface to the user
  });
}
